import React, { useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "primereact/button";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Image } from "primereact/image";
import zindigiLogo from "../../../assets/imgs/zindigi_logo.png";
import { handlePostRequest } from "../../../services/PostTemplate";
import { Dialog } from "primereact/dialog";

const ZindigiWallet = () => {
    const [saveBtnLoading, setSaveBtnLoading] = useState(false);
    const [otpDialogVisible, setOtpDialogVisible] = useState(false); // State to control OTP dialog visibility
    const history = useHistory();
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.authenticationSlice?.userId);
    const inputRefs = useRef([]);

    const validationSchema = Yup.object().shape({
        mobileNo: Yup.mixed().required("This field is required."),
        cnic: Yup.mixed().required("This field is required."),
    });

    const formik = useFormik({
        validationSchema: validationSchema,
        initialValues: {
            mobileNo: "",
            cnic: "",
        },
        onSubmit: async (data) => {
            setSaveBtnLoading(true);
            const payload = {
                userId,
                mobileNo: data.mobileNo,
                dateTime: new Date().toISOString(),
                cnic: data.cnic,
            };
            try {
                const response = await dispatch(handlePostRequest("api/zindigi/verifyAccountToLink", payload, false, false));
                if (response?.status === 200) {
                    console.log("Account linked successfully:", response);
                    toast.success("Account linked successfully");
                    setOtpDialogVisible(true); // Show OTP dialog
                } else {
                    console.warn("Failed to link account:", response);
                    toast.error("Failed to link account");
                }
                setSaveBtnLoading(false);
            } catch (error) {
                console.error("Error linking account:", error);
                toast.error("Error linking account");
                setSaveBtnLoading(false);
            }
        },
    });

    const otpValidationSchema = Yup.object({
        OTP: Yup.array()
            .of(Yup.string().matches(/^[0-9]?$/, "Each field must be a single digit."))
            .test("all-filled", "All fields must be filled and valid.", (values) => values.every((value) => /^[0-9]$/.test(value))),
    });

    const otpFormik = useFormik({
        validationSchema: otpValidationSchema,
        initialValues: { OTP: ["", "", "", "", ""] },
        onSubmit: async ({ OTP }) => {
            const otpPin = OTP.join("");
            const payload = {
                userId,
                mobileNo: formik.values.mobileNo,
                dateTime: new Date().toISOString(),
                cnic: formik.values.cnic,
                otpPin: otpPin,
            };
            try {
                const response = await dispatch(handlePostRequest("api/zindigi/linkAccount", payload, true, true));
                if (response?.status === 200) {
                    history.push("/wallet");
                } else {
                    toast.error("Failed to verify OTP");
                }
            } catch (error) {
                console.error("Error verifying OTP:", error);
                toast.error("Error verifying OTP");
            }
        },
    });

    const handleChange = (e, index) => {
        const value = e.target.value;
        if (!/^[0-9]$/.test(value) && value !== "") return;

        const otp = [...otpFormik.values.OTP];
        otp[index] = value;
        otpFormik.setFieldValue("OTP", otp);
        if (value && index < otp.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        const { key } = e;
        if (key === "ArrowLeft" && index > 0) {
            inputRefs?.current[index - 1]?.focus();
        } else if (key === "ArrowRight" && index < inputRefs?.current?.length - 1) {
            inputRefs?.current[index + 1]?.focus();
        } else if (key === "Backspace") {
            const otp = [...otpFormik.values.OTP];
            if (otp[index] === "" && index > 0) {
                inputRefs?.current[index - 1]?.focus();
            } else {
                otp[index] = "";
                otpFormik.setFieldValue("OTP", otp);
            }
        }
    };

    const getOtpFormErrorMessage = () => otpFormik.errors.OTP && otpFormik.touched.OTP && <small className="p-error">{otpFormik.errors.OTP}</small>;

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="flex login-main-body align-items-center justify-content-center  overflow-hidden">
                    <div className="login-div-border">
                        <div className="w-full surface-card py-8 px-5 sm:px-6">
                            <div className="text-center mb-5">
                                <Image src={zindigiLogo} alt="Image" height="80" className="w-18rem mb-5" />
                                <div className="text-3xl font-medium mb-3" style={{ color: "whitesmoke" }}>
                                    Link Account
                                </div>
                            </div>

                            <div className="login-div">
                                <div className="col-12">
                                    <div className="mb-3">
                                        <InputText id="mobileNo" type="text" name="mobileNo" placeholder="Enter Phone Number" value={formik.values.mobileNo} onChange={formik.handleChange} className="w-full mb-2 p-3" />
                                        {formik.touched.mobileNo && formik.errors.mobileNo && <small className="p-error">{formik.errors.mobileNo}</small>}
                                    </div>
                                    <div className="mb-3">
                                        <InputText id="cnic" type="text" name="cnic" placeholder="Enter Your CNIC" value={formik.values.cnic} onChange={formik.handleChange} className="w-full mb-2 p-3" />
                                        {formik.touched.cnic && formik.errors.cnic && <small className="p-error">{formik.errors.cnic}</small>}
                                    </div>
                                </div>
                                <Button label="Submit" loading={saveBtnLoading} type="submit" className="w-full mt-3 mb-3 p-3 text-xl font-medium" />
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <Dialog header="OTP Verification" visible={otpDialogVisible} style={{ width: "30vw", background: "blue" }} onHide={() => setOtpDialogVisible(false)}>
                <div className="otp-verification-container flex justify-content-center align-items-center flex-column mt-5">
                    <h2 className="otp-heading mb-3">OTP Verification</h2>
                    <p className="otp-message text-center mb-3">We have sent an OTP to your mobile number</p>
                    <label htmlFor="otp" className="mb-2">
                        Enter the 5-digit SMS OTP
                    </label>
                    <form className="flex flex-column justify-content-center" onSubmit={otpFormik.handleSubmit}>
                        <div className="flex justify-content-center mb-3">
                            {otpFormik.values.OTP.map((value, index) => (
                                <InputText
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    value={value}
                                    onChange={(e) => handleChange(e, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    maxLength={1}
                                    className={`otp-input mr-2 ${otpFormik.errors.OTP && otpFormik.touched.OTP ? "p-invalid" : ""}`}
                                    style={{ width: "40px", textAlign: "center" }}
                                />
                            ))}
                        </div>
                        {getOtpFormErrorMessage()}
                        <Button type="submit" label="Verify" className="p-button-primary mt-3" />
                    </form>
                </div>
            </Dialog>
        </>
    );
};

export default ZindigiWallet;
